import {useEffect, useState} from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import {
  BlueBlob,
  Card,
  FormattedMessageDiv,
  FormattedNumberDiv,
} from '@odmonorepo/core-components';
import {Stack, Button, Alert, Row, Col} from 'react-bootstrap';
import { dollarFormat, formatPhone, phoneToTelHref } from '@odmonorepo/odfunctions';
import { useNavigate } from '@tanstack/react-router';
import { getLDR, getSalesAgentInfo, useMerchantValidation, useParties } from 'merchant-api/spiceroad';
import { useOffers } from 'merchant-api/offer';
import { useApplication } from 'merchant-api/unified';
import { Offer } from 'merchant-api/types';

const PreApprovedRenewal = ({
  offer,
  hasAdvisor,
  formattedPhone,
}: {
  offer?: Offer | null;
  hasAdvisor: boolean;
  formattedPhone: string;
}) => {
  // Build the dial href in E.164 form (tel:+1...) so strict RFC 3966 parsers dial correctly,
  // while the visible label keeps the human-readable formattedPhone.
  const telHref = phoneToTelHref(formattedPhone);

  return (
    <Card bg="primary" text="light" className="text-center" dismissible>
      <Stack gap={3} className="align-items-center">
        <h3 className="text-white">
          <FormattedMessage
            id="notification.parCall.title"
            values={{ amount: dollarFormat(offer?.maxLineAmount ?? 0).split('.')[0] }}
          />
        </h3>
        <FormattedMessageDiv
          id={hasAdvisor ? 'notification.parCall.subtext.advisor' : 'notification.parCall.subtext.fallback'}
        />
        <Button
          variant="outline-light"
          href={telHref}
        >
          <FormattedMessage
            id="notification.parCall.cta"
            values={{ phoneNumber: <span className="text-nowrap">{formattedPhone}</span> }}
          />
        </Button>
      </Stack>
    </Card>
  );
};

export const OfferNotification = () => {
  const intl = useIntl();
  const navigate = useNavigate();
  const [ hasValidTaxId, setHasValidTaxId ] = useState<boolean>(true);
  const useApplicationProps = useApplication(["APPROVED"]);
  const { applicationData, isErrorApplication, isPAR } = useApplicationProps;
  const { isLoadingParties, isErrorParties, isMultiOwner } = useParties();
  const { tlOffer, locOffer, product, selectedOffer, isLoadingOffers, isErrorOffers, isPartnerChannel } = useOffers();
  const { salesAgentInfo, isSalesAgentInfoLoading } = getSalesAgentInfo();
  const { data, isLoadingMerchantValidation } = useMerchantValidation();
  const { loanDocRequest, ldrLoading } = getLDR();

  const fallbackPhone = intl.formatMessage({ id: 'notification.parCall.fallbackNumber' });
  const formattedPhone = salesAgentInfo?.phone ? formatPhone(salesAgentInfo.phone) : fallbackPhone;

  const parCallOffer: Offer | null | undefined = !tlOffer && !locOffer
    ? null
    : (tlOffer?.maxLineAmount ?? 0) >= (locOffer?.maxLineAmount ?? 0) ? tlOffer : locOffer;

  const showParCallBanner = Boolean(isPAR) && !selectedOffer && !!parCallOffer && hasValidTaxId;
  
  useEffect(() => {
    if (!isLoadingMerchantValidation && data) {
      setHasValidTaxId(!!data?.isValidTaxId);
    }
  }, [isLoadingMerchantValidation, data]);

  if (isLoadingMerchantValidation || isLoadingOffers || isLoadingParties || ldrLoading || isSalesAgentInfoLoading) return null;
  if (isErrorParties || isErrorOffers || isErrorApplication || isMultiOwner) return null;
  if (loanDocRequest?.some((ldr) => ldr.classification === 'CLOSING' && (ldr.status === 'REQUESTED' || ldr.status === 'MERCHANT_RESPONSE_SUBMITTED' || ldr.status === 'REJECTED'))) return null;

  const MaxOffer = (offer) => (
    <>
      <h4 className="text-secondary text-center">
        <FormattedNumberDiv
          prefix={intl.formatMessage({ id: 'notification.title.openOffer' })}
          currency="USD"
          style="currency"
          value={offer?.maxLineAmount}
          suffix="."
          maximumFractionDigits={0}
        />
      </h4>
      {hasValidTaxId &&
        <div>
          <Button onClick={() => navigate({ to: '/application/$id', params: { id: applicationData!.id }})}>
            <FormattedMessage id="notification.callToAction.openOffer"/>
          </Button>
        </div>
      }
    </>
  );

  const DualOrLOCOffer = (locOffer) => (
    <>
      <h4 className="text-secondary text-center">
        {locOffer?.maxLineAmount ? (
          <FormattedNumberDiv
            prefix={intl.formatMessage({ id: 'notification.title.locOffer' })}
            currency="USD"
            style="currency"
            value={locOffer?.maxLineAmount}
            suffix="."
            maximumFractionDigits={0}
          />
        ) : (
          <FormattedMessage id="notification.title.dualOffer" />
        )}
      </h4>
      <div>
        {hasValidTaxId ?
          <Button className='text-white'>
            <FormattedMessageDiv id="salesAgentInfo.phone" values={{
              phoneNumber: <a className='text-decoration-none' href={phoneToTelHref(formattedPhone)}>{formattedPhone}</a>}}/>
          </Button> :
          <Button className='text-white' href={intl.formatMessage({id: 'notification.csNumber.helpline'})}>
            <FormattedMessage id="notification.callToAction.csNumber" />
          </Button>}
      </div>
    </>
  );

  const RenderNotification = () => {
    if (selectedOffer) return <MaxOffer {...selectedOffer} />;
    if (!isPartnerChannel) {
      switch (product) {
        case 'DualProduct': return <DualOrLOCOffer />;
        case 'LineOfCredit': return <DualOrLOCOffer {...locOffer} />;
        case 'TermLoan': return <MaxOffer {...tlOffer} />;
        default: return null;
      }
    }
  };

  return (
    <div>
      {/* Hide the decorative blob only for the self-contained call-to-agent card (unpriced PAR).
          Priced PAR offers fall through to the generic banner and should show the blob like non-PAR. */}
      <BlueBlob hideBlob = {showParCallBanner}>
        {!hasValidTaxId &&
          <Alert className="text-start" variant={"danger"} dismissible={false}>
            <FormattedMessageDiv className="alert-heading text-start" id="notification.alert.title" />
            <FormattedMessageDiv
              id="notification.alert.body"
              values={{
                agentName: salesAgentInfo?.name ? salesAgentInfo.name : "us",
                agentPhoneNumber: <a className="text-decoration-underline" href={phoneToTelHref(formattedPhone)}>{formattedPhone}</a>
            }}/>
          </Alert>
        }
        <Stack gap={3}>
          {showParCallBanner ? (
            <PreApprovedRenewal offer={parCallOffer} hasAdvisor={!!salesAgentInfo?.phone} formattedPhone={formattedPhone} />
          ) : (
            <RenderNotification />
          )}
        </Stack>
      </BlueBlob>
    </div>
  );
};

export default OfferNotification;
